import { useCallback } from 'react';

import { useApolloClient } from '@apollo/client';

import { IRestaurant } from '@rbi-ctg/store';
import { GetRestaurantDocument } from 'generated/graphql-gateway';
import logger from 'utils/logger';

import { IGetRestaurantFn, QueryResult } from '../types';

import { useGetStoreAvailabilityFn } from './use-get-restaurant-availability-fn';

/**
 * Utility hook that provides a function to query an rbiRestaurant by id
 *
 * @returns {IGetRestaurantFn}
 * Function that retrieves an RbiRestaurant with the most up to date data
 * It will pass the restaurant through `ICheckAvailability` before returning
 */
export const useGetStoreFn = (): IGetRestaurantFn => {
  const client = useApolloClient();
  const checkAvailability = useGetStoreAvailabilityFn();

  const getRestaurantFn = useCallback<IGetRestaurantFn>(
    async (storeId: string | null) => {
      if (!storeId) {
        return;
      }
      const { data, errors }: QueryResult = await client.query({
        // we always want the most up to date data when calculating heartbeat freshness
        fetchPolicy: 'network-only',
        variables: { storeId },
        query: GetRestaurantDocument,
      });

      if (!data || errors?.length) {
        const message = `Failed to query 'GetRestaurant' with 'storeId: ${storeId}'`;

        if (errors?.length) {
          logger.error({
            errors,
            message,
          });
        } else {
          logger.error(message);
        }

        return;
      }

      const { restaurant } = data;

      return {
        ...restaurant,
        available: checkAvailability(restaurant as IRestaurant),
      };
    },
    [checkAvailability, client]
  );

  return getRestaurantFn;
};
